<template>
  <div class="main-container">
    <!-- ───────── HEADER ───────── -->
    <v-row class="pa-2 header-row">
      <v-col cols="12" class="d-flex justify-space-between align-center">
        <!-- Back -->
        <v-btn icon class="back-button my-4 mx-4" @click="confirmBackNavigation">
          <v-icon class="white--text mx-4">mdi-arrow-left</v-icon>
        </v-btn>

        <!-- Add PO (allowed when Add_Button === true) -->
        <v-btn
          v-if="canAddPO"
          color="#ff3b47"
          width="150px"
          dense
          dark
          class="add-po-button mx-4"
          @click="navigateToAddPO"
        >
          Add PO
        </v-btn>
      </v-col>
    </v-row>

    <!-- ───────── LOADER ───────── -->
    <v-row v-if="loading" justify="center" align="center" class="loader-row">
      <v-progress-circular indeterminate color="#ff3b47" />
    </v-row>

    <!-- ───────── CONTENT ───────── -->
    <div v-else class="po-container">
      <!-- Search + Filter -->
      <v-row justify="center" class="search-filter-row">
        <v-col cols="12" md="4">
          <v-text-field
            v-model="search"
            class="pa-3 white-search-field"
            filled dense rounded
            append-icon="mdi-magnify"
            label="Search POs"
            single-line hide-details
          />
        </v-col>
        <v-col cols="12" md="4">
          <v-select
            v-model="statusFilter"
            :items="statuses"
            class="pa-3 white-search-field"
            filled dense rounded
            label="Filter by Status"
            single-line hide-details
          />
        </v-col>
      </v-row>

      <!-- PO cards -->
      <v-row>
        <v-col
          v-for="po in filteredPOs"
          :key="po.id"
          cols="12" sm="6" md="4" lg="3"
        >
          <v-card
            class="modern-card"
            elevation="6"
            hover
            :class="{ 'cursor-pointer': canEditPO || canViewPO }"
            @click="(canEditPO || canViewPO) ? navigateToPO(po) : null"
          >
            <!-- Chips + copy -->
            <v-row class="d-flex justify-end pr-3 pt-3">
              <v-chip
                :color="getChipColor(po.status)"
                dark label
                class="status-chip ml-2"
              >
                {{ po.status || 'Unknown' }}
              </v-chip>
              <v-chip color="grey" dark label class="purchase-number-chip ml-2">
                No.{{ po.purchaseNumber || '0000' }}
              </v-chip>
            </v-row>

            <!-- Body (expanded) -->
            <v-card-text class="pa-4">
              <v-row>
                <v-col cols="6" class="py-1">
                  <span class="text-caption">Priority:</span><br />
                  <span class="font-weight-medium">{{ po.priority }}</span>
                </v-col>
             
              </v-row>
              <v-row>
                <v-col cols="6" class="py-1">
                  <span class="text-caption">Supplier:</span><br />
                  <span class="font-weight-medium">{{ po.supplier }}</span>
                </v-col>
                <v-col cols="6" class="py-1">
                  <span class="text-caption">SO #:</span><br />
                  <span class="font-weight-medium">{{ po.soNumber || '—' }}</span>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="6" class="py-1">
                  <span class="text-caption">Created:</span><br />
                  <span class="font-weight-medium">
                    {{ formatDate(po.createdAt) }}
                  </span>
                </v-col>
                <v-col cols="6" class="py-1">
                  <span class="text-caption">Items:</span><br />
                  <span class="font-weight-medium">
                    {{ po.itemsCount ?? '-' }}
                  </span>
                </v-col>
              </v-row>
            </v-card-text>

            <!-- Action button -->
            <v-card-actions v-if="canEditPO || canViewPO">
              <v-btn
                width="100%"
                rounded
                dark
                color="#ff3b47"
                class="open-po-button"
                @click.stop="navigateToPO(po)"
              >
                {{ canEditPO ? 'Edit PO' : 'View PO' }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <!-- ───── Back confirm ───── -->
    <v-dialog v-model="backDialog" max-width="500">
      <v-card>
        <v-card-title class="headline">Confirm Navigation</v-card-title>
        <v-card-text>
          Are you sure you want to go back? Unsaved changes might be lost.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="red" text @click="backDialog = false">Cancel</v-btn>
          <v-btn color="green" text @click="goBack">Yes, Go Back</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ───── Snackbar ───── -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3000">
      {{ snackbarMessage }}
      <v-btn color="white" text @click="snackbar = false">Close</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import { format } from 'date-fns';
import {
  getFirestore,
  collection,
  getDocs,
  doc,
  getDoc
} from 'firebase/firestore';
import {
  getAuth,
  onAuthStateChanged
} from 'firebase/auth';
import { firebaseApp } from '@/firebaseConfig';

const db = getFirestore(firebaseApp);
const auth = getAuth(firebaseApp);

export default {
  name: 'POComponent',

  data() {
    return {
      loading: true,
      backDialog: false,
      search: '',
      statusFilter: '',
      statuses: ['Clear Filter', 'In Progress', 'Delayed', 'Complete'],
      snackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success',

      pos: [],
      userPerms: {}
    };
  },

  computed: {
    isAllowed() {
      return v => v === true || v === 'true' || v === 1 || v === '1';
    },
    canAddPO() {
      return this.isAllowed(this.userPerms.Add_Button);
    },
    canEditPO() {
      return this.isAllowed(this.userPerms.Edit_Details);
    },
    canViewPO() {
      return (
        this.isAllowed(this.userPerms.View_in_Functions) ||
        this.isAllowed(this.userPerms.View_in_Activities)
      );
    },
    filteredPOs() {
      let list = [...this.pos];
      if (this.search) {
        const q = this.search.toLowerCase();
        list = list.filter(po =>
          [po.purchaseNumber, po.priority, po.supplier, po.status]
            .some(val => val && val.toString().toLowerCase().includes(q))
        );
      }
      if (this.statusFilter && this.statusFilter !== 'Clear Filter') {
        list = list.filter(po => po.status === this.statusFilter);
      }
      return list.sort((a, b) => b.purchaseNumber - a.purchaseNumber);
    }
  },

  async created() {
    await this.loadPermissions();
    await this.fetchPOs();
    this.loading = false;
  },

  methods: {
    async loadPermissions() {
      try {
        await new Promise(resolve => {
          onAuthStateChanged(auth, async user => {
            if (user) {
              const snap = await getDoc(doc(db, 'users', user.uid));
              if (snap.exists()) {
                this.userPerms = snap.data().purchaseOrders || {};
              }
            }
            resolve();
          });
        });
      } catch (err) {
        console.error(err);
        this.showSnackbar('Failed to load permissions.', 'error');
      }
    },

    async fetchPOs() {
      try {
        const qs = await getDocs(collection(db, 'purchaseOrders'));
        this.pos = qs.docs.map(d => {
          const data = d.data();
          return {
            id: d.id,
            purchaseNumber: data.purchaseNumber ?? '0000',
            priority: data.priority ?? 'Unknown',
            supplier: data.supplier ?? 'Unknown',
            status: data.status ?? 'Unknown',
            deliveryTime: data.deliveryTime ?? 'TBD',
            soNumber: data.soNumber || null,
            createdAt: data.createdAt?.toDate?.() || new Date(),
            itemsCount: Array.isArray(data.items) ? data.items.length : null
          };
        });
      } catch (err) {
        console.error(err);
        this.showSnackbar('Error fetching purchase orders.', 'error');
      }
    },

    getChipColor(status = '') {
      switch (status.toLowerCase()) {
        case 'complete':
        case 'completed':
          return 'green';
        case 'in progress':
          return 'purple';
        case 'delayed':
          return 'red';
        case 'pending':
          return 'orange';
        default:
          return 'grey';
      }
    },

    copyCardContent(po) {
      const txt = `Purchase Number: ${po.purchaseNumber}\nStatus: ${po.status}\nPriority: ${po.priority}\nSupplier: ${po.supplier}`;
      navigator.clipboard.writeText(txt)
        .then(() => this.showSnackbar('Card content copied ✅'))
        .catch(() => this.showSnackbar('Copy failed.', 'error'));
    },

    showSnackbar(msg, type = 'success') {
      this.snackbarMessage = msg;
      this.snackbarColor = type === 'error' ? 'error' : 'success';
      this.snackbar = true;
    },

    confirmBackNavigation() {
      this.backDialog = true;
    },
    goBack() {
      this.backDialog = false;
      this.$router.go(-1);
    },
    navigateToAddPO() {
      this.$router.push({ name: 'AddPO' });
    },

    navigateToPO(po) {
      localStorage.setItem('selectedPO', po.id);
      if (this.canEditPO) this.$router.push({ name: 'EditPO' });
      else if (this.canViewPO) this.$router.push({ name: 'ViewPO' });
    },

    formatDate(date) {
      try {
        return format(date, 'MMM d, yyyy');
      } catch {
        return '-';
      }
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap');

.main-container { background:#121212; min-height:100vh; }
.header-row     { background:#1e1e1e; border-bottom:1px solid #333; }
.po-container   {
  background:#fff; border-radius:20px 20px 0 0; padding:24px;
  box-shadow:0 4px 15px rgba(0,0,0,.1); min-height:70%;
}
.loader-row        { margin-top:50px; }
.search-filter-row { margin-bottom:24px; }

.modern-card {
  background:#fff; border-radius:16px; overflow:hidden; margin:8px;
  transition:transform .3s, box-shadow .3s;
  box-shadow:0 4px 15px rgba(0,0,0,.1);
}
.modern-card:hover {
  transform:translateY(-5px);
  box-shadow:0 8px 25px rgba(0,0,0,.2);
}
.status-chip, .purchase-number-chip { font-size:.75rem; }
.text-caption       { font-size:.8rem; color:#757575; }
.font-weight-medium { font-weight:500; color:#333; }

.back-button { color:#fff; border-radius:50%; transition:transform .3s; }
.back-button:hover { transform:scale(1.1); }
.add-po-button, .open-po-button {
  font-weight:700; transition:background-color .3s, box-shadow .3s;
}
.add-po-button:hover  { background:#e02834; box-shadow:0 6px 12px rgba(0,0,0,.15); }
.open-po-button:hover { background:#e02834; transform:scale(1.02); }

.white-search-field .v-input__control { background:#fff; border-radius:8px; }
.white-search-field .v-label,
.white-search-field .v-input__icon--append .v-icon { color:#555; }

.v-dialog .v-card { border-radius:16px; }
</style>
